import { _js, ___p } from '@ifixit/utils/localize';
import { Badge } from '@ifixit/ui/components/badge';
import { FaIcon } from '@ifixit/ui/components/icon';
import {
   Pagination,
   PaginationContent,
   PaginationItem,
   PaginationLink,
   PaginationNext,
   PaginationPrevious,
   calculatePageRange,
} from '@ifixit/ui/components/pagination';
import { cn } from '@ifixit/ui/lib/utils';
import {
   type KeyboardEvent as ReactKeyboardEvent,
   type MouseEvent as ReactMouseEvent,
   useEffect,
   useMemo,
   useRef,
   useState,
} from 'react';
import { localizeAndFormat } from 'Shared/utils/numberFormat';
import type {
   FlaggedPageData,
   HelpOutFlag,
   HelpOutFlaggedPage,
   HelpOutPhoto,
   HelpOutProps,
} from './help_out_types';
import { useFlaggedPages } from './use_help_out_api';

/**
 * /Participate, the first tab of the Help Out row.
 *
 * Two blocks, split by who can do the work rather than by how big the
 * pile is. The three photographic cards are the jobs that need no
 * reputation, so a first visit shows three open doors; the rows under
 * them are the review work, each with what is waiting on it. The card is
 * the one the page has always used, down to the photograph bleeding past
 * the padding and the badge hanging off its bottom edge. What is new is
 * the count in the corner of each photograph.
 *
 * Everything renders from props: the page has no queue to page through,
 * so there is no fetching here and no client state.
 */

/**
 * AddressFlag caps each flag list at 50 rows, so a total built out of them
 * is a floor. Say so with a "+" rather than print a number the page cannot
 * stand behind.
 */
function formatCount(count: number, approximate?: boolean): string {
   return approximate ? `${localizeAndFormat(count)}+` : localizeAndFormat(count);
}

/** The shell Leaderboard, Translate, Patrol and Moderation all lay out in. */
const SHELL_CLASS = 'mx-auto w-full max-w-5xl px-4 pb-10 md:px-8';

/**
 * Section headings sat at the same 20px medium as the card titles under
 * them, so the page read as one flat list of blocks. They outrank the
 * cards now, and the cards stepped down to 18 to make room.
 */
const SECTION_HEADING_CLASS = 'mt-12 text-2xl font-semibold tracking-tight text-gray-950';

/** The filter value that means every flag, matching FlaggedPages::ALL_FLAGS. */
const ALL_FLAGS = 'all';

/** What the card above jumps to. Not a flagid, so readView ignores it. */
const FLAGGED_SECTION_ID = 'flagged';

/** The editors' SectionCard weight, full-bleed with square corners below md. */
const CARD_CLASS =
   'overflow-hidden rounded-2xl border border-gray-300 bg-white max-md:-mx-4 max-md:rounded-none max-md:border-x-0';

export function HelpOutPage(props: HelpOutProps) {
   return (
      <div className={SHELL_CLASS}>
         <header className="pt-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
               <h1 className="heading-30">{_js('Help Out')}</h1>
               {!props.isLoggedIn && (
                  <a
                     href={props.urls.join}
                     className="button-primary button-md no-underline hover:no-underline"
                  >
                     {_js('Join the community')}
                  </a>
               )}
            </div>
            <p className="mt-2 max-w-2xl text-sm text-gray-700">
               {_js(
                  'Free repair information only exists because people write it, check it and translate it. Pick something to start with.'
               )}{' '}
               <a
                  href={props.urls.help}
                  className="whitespace-nowrap font-medium text-brand-500 underline-offset-2 hover:text-brand-600 hover:underline"
               >
                  {_js('How contributing works')}
               </a>
            </p>
         </header>

         <StartHere {...props} />
         <WriteAGuide {...props} />
         <ReviewWork {...props} />
         <FlaggedQueue {...props} />
      </div>
   );
}

/**
 * The three jobs anyone can start. A card only appears when the site
 * actually offers that job, so a nanosite with the answers forum off gets
 * two cards rather than a dead one.
 */
function StartHere(props: HelpOutProps) {
   const cards = [
      props.showAnswers && (
         <JobCard
            key="answers"
            photo={props.images.answers}
            alt={_js('Hands working on an opened laptop, with tools laid out beside it')}
            icon="comments-question-check"
            title={_js('Answer a question')}
            text={_js(
               'Someone is halfway through a repair with the case already open. If you have done that fix, you can get them moving again.'
            )}
            count={props.counts.unanswered}
            countLabel={_js('waiting')}
            cta={_js('Answer one')}
            href={props.urls.answers}
         />
      ),
      props.counts.untranslated !== null && (
         <JobCard
            key="translate"
            lazy
            photo={props.images.translate}
            alt={_js('A tablet showing a repair guide, next to an open toolkit')}
            icon="language"
            title={_js('Translate a guide')}
            text={_js(
               'The machine does the first pass. You read it in your own language and fix what it got wrong, one string at a time.'
            )}
            count={props.counts.untranslated}
            countLabel={_js('untranslated')}
            cta={_js('Start translating')}
            href={props.urls.translate}
         />
      ),
      <JobCard
         key="flagged"
         lazy
         photo={props.images.flagged}
         alt={_js('Two people at a cafe table with phones opened up in front of them')}
         icon="screwdriver-wrench"
         title={_js('Fix a flagged page')}
         text={_js(
            'Someone read a guide and marked what was wrong with it: a missing step, a photo you cannot make out, a tool named incorrectly.'
         )}
         count={props.counts.flaggedGuides}
         countLabel={_js('flagged')}
         approximate={props.counts.flaggedGuidesCapped}
         cta={_js('See what needs fixing')}
         href={`#${FLAGGED_SECTION_ID}`}
      />,
   ].filter(Boolean);

   return (
      // One column until the whole row fits. The 2-up step in between left
      // three cards as two and a widow, which read as a card that had
      // failed to load rather than as a deliberate row.
      <div
         className={cn(
            'mt-5 grid grid-cols-1 gap-5',
            cards.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'
         )}
      >
         {cards}
      </div>
   );
}

/**
 * Writing a guide is the one job with no queue behind it, so it gets a
 * banner rather than a fourth card: every card in the row above carries a
 * number, and one that could not would have read as a card whose count
 * failed to load.
 */
function WriteAGuide(props: HelpOutProps) {
   return (
      <div className="mt-5 grid grid-cols-1 overflow-hidden rounded-2xl border border-gray-300 bg-white md:grid-cols-[14rem_minmax(0,1fr)]">
         <img
            className="h-32 w-full object-cover md:h-full"
            srcSet={props.images.guideNew.srcset}
            sizes="(min-width: 768px) 224px, calc(100vw - 2rem)"
            loading="lazy"
            src={props.images.guideNew.src}
            alt={_js('Two people at a table working on a small repair together')}
            width="500"
            height="333"
         />
         <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
            <div className="min-w-0">
               <h2 className="text-xl font-medium text-gray-950">{_js('Write a guide')}</h2>
               <p className="mt-1 max-w-xl text-sm text-gray-600">
                  {_js(
                     'No one knows how to fix everything, but everyone knows how to fix something. Photograph the next repair you do and write it down.'
                  )}{' '}
                  {/* Unfinished drafts belong here rather than among the
                      flagged pages: nobody read these and reported a
                      problem with them, they were just never finished. */}
                  {props.counts.inProgress ? (
                     <a
                        href={props.inProgressUrl}
                        className="font-medium text-brand-500 underline-offset-2 hover:text-brand-600 hover:underline"
                     >
                        {___p(
                           props.counts.inProgress,
                           'Or finish the guide someone started',
                           'Or finish one of the %1 guides someone started',
                           formatCount(props.counts.inProgress, props.counts.inProgressCapped)
                        )}
                     </a>
                  ) : null}
               </p>
            </div>
            <a
               href={props.urls.guideNew}
               className="inline-flex h-9 shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white px-4 font-semibold text-gray-900 no-underline transition-colors hover:bg-gray-50 hover:no-underline"
            >
               {_js('Create a guide')}
            </a>
         </div>
      </div>
   );
}

/**
 * The live card, unchanged in shape: the photograph bleeds 20px past the
 * card's horizontal padding, and the badge hangs 28px off its bottom edge.
 *
 * The one departure from the live card is the phone. That one hides the
 * photograph below md; this keeps a 120px crop, because on a phone the
 * picture is the only thing above the fold and dropping it takes the
 * visual block away exactly where it does the most work.
 */
function JobCard({
   photo,
   alt,
   icon,
   title,
   text,
   count,
   countLabel,
   approximate,
   cta,
   href,
   lazy,
}: {
   photo: HelpOutPhoto;
   alt: string;
   icon: string;
   title: string;
   text: string;
   count: number | null;
   countLabel?: string;
   approximate?: boolean;
   cta: string;
   href: string;
   /** Below the first screen on a phone. */
   lazy?: boolean;
}) {
   return (
      <div className="relative flex flex-col overflow-hidden rounded-2xl border border-gray-300 bg-white text-gray-800">
         {/* The photograph is a plain full-width block and the padding
             lives on the content below it. It used to bleed past the card
             on a negative margin with a calc() width, which is the only
             thing on the page that was ever wider than its parent. */}
         <div className="relative">
            <img
               className="h-[120px] w-full object-cover md:h-[215px]"
               srcSet={photo.srcset}
               sizes="(min-width: 1024px) 480px, calc(100vw - 2rem)"
               loading={lazy ? 'lazy' : undefined}
               src={photo.src}
               alt={alt}
               width="500"
               height="215"
            />
            {count !== null && (
               <span className="absolute top-2.5 right-2.5 inline-flex items-center rounded-full bg-white/95 px-2.5 py-0.5 text-xs font-semibold text-brand-700 tabular-nums shadow-sm">
                  {countLabel
                     ? `${formatCount(count, approximate)} ${countLabel}`
                     : formatCount(count, approximate)}
               </span>
            )}
         </div>
         <div className="flex flex-1 flex-col px-3 pb-3 md:px-5 md:pb-6">
            {/* Below md the badge sits in the photograph's top-left corner,
                opposite the count, and the text gets the full card width.
                From md it hangs 28px off the bottom edge of the
                photograph, centered, the way the card has always drawn it.

                Positioned in both states on purpose: the count pill needs a
                positioned wrapper around the photograph, and a positioned
                element paints over a static one whatever the DOM order, so
                a static badge would have its top half covered. */}
            <div className="absolute top-2.5 left-2.5 flex size-10 flex-none items-center justify-center overflow-hidden rounded-md border border-brand-200 bg-brand-100 text-brand-500 md:relative md:top-auto md:left-auto md:mx-auto md:mt-[-28px] md:mb-6 md:size-14">
               <FaIcon name={icon} family="duotone" className="size-5 md:size-7" aria-hidden />
            </div>
            <div className="flex grow flex-col pt-3 text-start md:pt-0 md:text-center lg:min-h-[132px]">
               <h2 className="m-0 text-lg leading-[140%] font-medium text-gray-800">{title}</h2>
               <p className="mx-auto my-0 grow pt-1.5 text-gray-600 md:pt-2">{text}</p>
            </div>
            <a
               href={href}
               className="mt-4 inline-flex h-9 w-full items-center justify-center rounded-lg bg-brand-500 px-4 font-semibold text-white no-underline transition-colors hover:bg-brand-600 hover:no-underline md:mt-6"
            >
               {cta}
            </a>
         </div>
      </div>
   );
}

/**
 * The review work: one row per job, count on the right, so the eye runs
 * down a single column to find the biggest pile. A job the viewer cannot
 * reach yet says what unlocks it instead of a number.
 */
function ReviewWork(props: HelpOutProps) {
   const { counts, urls } = props;
   const locked = !props.canVote;

   const rows = [
      counts.translationReviews !== null && (
         <ReviewRow
            key="translationReviews"
            href={urls.translate}
            icon="language"
            title={_js('Review translations')}
            text={_js('Strings a translator finished, waiting on a second reader.')}
            count={counts.translationReviews}
         />
      ),
      props.showPatrol && (
         <ReviewRow
            key="edits"
            href={urls.patrol}
            icon="check-double"
            title={_js('Check community edits')}
            text={_js('Edits to guides and device pages that need a second pair of eyes.')}
            count={counts.edits}
         />
      ),
      props.showModeration && (
         <ReviewRow
            key="pendingVotes"
            href={urls.moderation}
            icon={locked ? 'lock' : 'gavel'}
            title={_js('Vote on flagged posts')}
            text={
               locked
                  ? reputationHint(props)
                  : _js('Posts and comments the community reported, waiting on enough votes.')
            }
            count={locked ? null : counts.pendingVotes}
            lockLabel={
               locked ? _js('Needs %1 rep', localizeAndFormat(props.voteThreshold)) : undefined
            }
         />
      ),
   ].filter(Boolean);

   return (
      <>
         <h2 className={SECTION_HEADING_CLASS}>{_js('Waiting for review')}</h2>
         <p className="mt-1 max-w-2xl text-sm text-gray-600">
            {_js('Work that needs a second pair of eyes before it counts.')}
         </p>
         <div className={cn(CARD_CLASS, 'mt-4')}>
            <ul role="list" className="my-0 list-none divide-y divide-gray-200 pl-0">
               {rows}
            </ul>
         </div>
      </>
   );
}

/**
 * What the viewer still needs to vote. Signed out, the bar is the whole
 * message; signed in, saying where they already are makes the gap
 * something they can close.
 */
function reputationHint(props: HelpOutProps): string {
   if (props.viewerReputation === null) {
      return _js('Sign in and earn reputation by answering questions and writing guides.');
   }
   return _js(
      'You have %1. Answering questions is the fastest way there.',
      localizeAndFormat(props.viewerReputation)
   );
}

function ReviewRow({
   href,
   icon,
   title,
   text,
   count,
   lockLabel,
   approximate,
}: {
   href: string;
   icon: string;
   title: string;
   text: string;
   count: number | null;
   lockLabel?: string;
   approximate?: boolean;
}) {
   const isLocked = lockLabel !== undefined;
   return (
      <li>
         <a
            href={href}
            className={cn(
               'grid grid-cols-[2rem_minmax(0,1fr)_auto] items-center gap-x-3 px-4 py-3 no-underline hover:bg-gray-50 hover:no-underline md:grid-cols-[2rem_minmax(0,1fr)_auto_1rem]',
               'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50'
            )}
         >
            <span
               className={cn(
                  'row-span-2 flex size-8 items-center justify-center rounded-lg',
                  isLocked ? 'bg-gray-100 text-gray-400' : 'bg-brand-50 text-brand-500'
               )}
            >
               <FaIcon name={icon} className="size-4" aria-hidden />
            </span>
            <span
               className={cn('text-sm font-medium', isLocked ? 'text-gray-500' : 'text-gray-950')}
            >
               {title}
            </span>
            {isLocked ? (
               <span className="row-span-2 self-center text-xs text-gray-500">{lockLabel}</span>
            ) : count === null ? (
               <span className="row-span-2" />
            ) : (
               <Badge className="badge-sm badge-gray row-span-2 self-center tabular-nums">
                  {formatCount(count, approximate)}
               </Badge>
            )}
            <span className="row-span-2 hidden text-gray-400 md:flex">
               <FaIcon name="chevron-right" className="size-3.5" aria-hidden />
            </span>
            <span
               className={cn(
                  // Column 2 only: the count beside it now spans both rows.
                  'col-start-2 col-end-3 text-xs text-gray-500'
               )}
            >
               {text}
            </span>
         </a>
      </li>
   );
}

/**
 * The flagged pages: the flags as a filter, the pages as a table, and a
 * pager that goes as deep as the queue does.
 *
 * These were two sections, a grid of chips that linked away and a list of
 * twelve names that could not be filtered. They are a filter and its
 * results, so they are one section now, and clicking a flag changes the
 * table where it stands instead of leaving the page.
 *
 * The pill shape, the selected state and the tabular rows are the ones
 * /Translate uses for its three queues.
 */
function FlaggedQueue(props: HelpOutProps) {
   // A string, not the array: the effect that listens for Back would
   // re-subscribe on every render against a fresh array.
   const knownFlags = useMemo(() => props.flags.map(flag => flag.id).join(','), [props.flags]);
   const [view, setView] = useFlaggedView(knownFlags);
   const query = useFlaggedPages(view.flag, view.page, props.flagged);
   const data = query.data;
   const headingRef = useRef<HTMLHeadingElement>(null);
   const openedOnFlag = useRef(view.flag !== ALL_FLAGS);

   // No element carries a flag's id, so the browser leaves a link like
   // /Participate#GUIDE_MISSING_STEPS at the top of the page.
   useEffect(() => {
      if (openedOnFlag.current) {
         headingRef.current?.scrollIntoView();
      }
   }, []);

   if (props.flags.length === 0) {
      return null;
   }

   return (
      <>
         <h2
            id={FLAGGED_SECTION_ID}
            ref={headingRef}
            className={cn(SECTION_HEADING_CLASS, 'scroll-mt-4')}
         >
            {_js('Fix a flagged page')}
         </h2>
         <p className="mt-1 max-w-2xl text-sm text-gray-600">
            {_js('Someone read these and marked what was wrong. Most-read first.')}
         </p>

         <FlagFilters
            flags={props.flags}
            everything={props.counts.flaggedGuides}
            everythingCapped={props.counts.flaggedGuidesCapped}
            active={view.flag}
            onSelect={flag => setView({ flag, page: 1 })}
         />

         <div
            id="flagged-panel"
            role="tabpanel"
            aria-labelledby={`flag-tab-${view.flag}`}
            aria-busy={query.isPlaceholderData}
            className={cn('mt-4 transition-opacity', query.isPlaceholderData && 'opacity-60')}
         >
            {data ? (
               <FlaggedTable data={data} activeFlag={view.flag} />
            ) : (
               <div className={cn(CARD_CLASS, 'p-8 text-center text-sm text-gray-500')}>
                  {query.isError && !query.isFetching ? (
                     <>
                        <p className="my-0">{_js('That list would not load.')}</p>
                        <button
                           type="button"
                           className="button-outline button-sm mt-3"
                           onClick={() => {
                              void query.refetch();
                           }}
                        >
                           {_js('Try again')}
                        </button>
                     </>
                  ) : (
                     _js('Loading')
                  )}
               </div>
            )}
            {data && (
               <FlaggedPager data={data} onPage={page => setView({ flag: view.flag, page })} />
            )}
         </div>
      </>
   );
}

/**
 * The filter and the page live in the URL, so a link to one flag's third
 * page survives a reload and the back button. The flag rides the hash the
 * way /Translate's queues do; the page rides a query param, like theirs.
 */
function useFlaggedView(knownFlags: string) {
   const [view, setLocal] = useState(() => readView(knownFlags));

   useEffect(() => {
      const onPop = () => setLocal(readView(knownFlags));
      window.addEventListener('popstate', onPop);
      return () => window.removeEventListener('popstate', onPop);
   }, [knownFlags]);

   const setView = (next: { flag: string; page: number }) => {
      setLocal(next);
      const url = new URL(window.location.href);
      url.hash = next.flag === ALL_FLAGS ? '' : next.flag;
      if (next.page > 1) {
         url.searchParams.set('page', String(next.page));
      } else {
         url.searchParams.delete('page');
      }
      window.history.pushState(null, '', url.pathname + url.search + url.hash);
   };

   return [view, setView] as const;
}

/**
 * Read on mount as well as on Back, so a link someone was sent opens on
 * the flag it names. The hash is checked against the flags this site
 * actually has: anything else, including an anchor like #flagged, falls
 * back to everything rather than asking the server about a flag it will
 * refuse.
 */
function readView(knownFlags: string) {
   const hash = window.location.hash.replace(/^#/, '');
   const page = Number(new URLSearchParams(window.location.search).get('page'));
   return {
      // The empty check carries its weight: ''.split(',') is [''], so a
      // site with no flags at all matched an empty hash and asked the
      // server for a flag named nothing.
      flag: hash && knownFlags.split(',').includes(hash) ? hash : ALL_FLAGS,
      page: Number.isInteger(page) && page > 0 ? page : 1,
   };
}

/** Every flag with anything in it, biggest pile first, plus an "all". */
function FlagFilters({
   flags,
   everything,
   everythingCapped,
   active,
   onSelect,
}: {
   flags: HelpOutFlag[];
   everything: number;
   everythingCapped: boolean;
   active: string;
   onSelect: (flag: string) => void;
}) {
   const pillsRef = useRef<Array<HTMLButtonElement | null>>([]);
   const options = [
      {
         id: ALL_FLAGS,
         name: _js('Everything'),
         icon: 'bars',
         count: everything,
         capped: everythingCapped,
      },
      ...flags.map(flag => ({
         id: flag.id,
         name: flag.name,
         icon: flag.icon ?? 'flag',
         count: flag.count,
         capped: flag.capped,
      })),
   ];

   // Arrows move focus only; Enter or Space commits, so arrowing past a
   // flag on the way to another does not fetch it.
   const onKeyDown = (event: ReactKeyboardEvent, index: number) => {
      const next = {
         ArrowRight: index + 1,
         ArrowLeft: index - 1,
         Home: 0,
         End: options.length - 1,
      }[event.key];
      if (next === undefined) {
         return;
      }
      event.preventDefault();
      pillsRef.current[(next + options.length) % options.length]?.focus();
   };

   return (
      <div
         role="tablist"
         aria-label={_js('Filter by flag')}
         className="scroll-fade-x mt-3 -mb-1 flex gap-2 py-1 md:flex-wrap"
      >
         {options.map((option, index) => {
            const isActive = option.id === active;
            return (
               <button
                  key={option.id}
                  ref={element => {
                     pillsRef.current[index] = element;
                  }}
                  type="button"
                  role="tab"
                  id={`flag-tab-${option.id}`}
                  aria-selected={isActive}
                  aria-controls="flagged-panel"
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => !isActive && onSelect(option.id)}
                  onKeyDown={event => onKeyDown(event, index)}
                  className={cn(
                     'inline-flex items-center gap-1.5 rounded-2xl border bg-white px-2.5 py-1.5 text-sm font-medium transition-colors',
                     'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                     // The inset shadow doubles the border into a 2px stroke
                     // without shifting the pill or fighting the focus ring.
                     isActive
                        ? 'border-brand-500 text-brand-500 shadow-[inset_0_0_0_1px_var(--color-brand-500)]'
                        : 'border-gray-200 text-gray-900 hover:border-gray-300'
                  )}
               >
                  <FaIcon name={option.icon} className="size-3.5" aria-hidden />
                  <span className="min-w-0 truncate">{option.name}</span>
                  <Badge
                     className={cn(
                        'badge-sm tabular-nums',
                        isActive ? 'badge-brand' : 'badge-gray'
                     )}
                  >
                     {formatCount(option.count, option.capped)}
                  </Badge>
               </button>
            );
         })}
      </div>
   );
}

/** The desktop column template the rows and the header band share. */
const QUEUE_GRID_CLASS =
   'md:grid md:grid-cols-[3.75rem_minmax(0,1fr)_15rem_4.5rem] md:items-center md:gap-x-3';

function FlaggedTable({ data, activeFlag }: { data: FlaggedPageData; activeFlag: string }) {
   if (data.items.length === 0) {
      return (
         <div className={cn(CARD_CLASS, 'p-8 text-center text-sm text-gray-500')}>
            {_js('Nothing carries this flag right now.')}
         </div>
      );
   }

   return (
      <div className={CARD_CLASS}>
         <div
            className={cn(
               'hidden border-b border-gray-200 bg-gray-100 px-4 py-2',
               QUEUE_GRID_CLASS
            )}
         >
            <span className="text-xs font-medium text-gray-600">{_js('Type')}</span>
            <span className="text-xs font-medium text-gray-600">{_js('Page')}</span>
            <span className="text-xs font-medium text-gray-600">{_js('What it needs')}</span>
            <span className="text-right text-xs font-medium text-gray-600">
               <span aria-hidden="true">{_js('Views')}</span>
               <span className="sr-only">{_js('Views in the last 30 days')}</span>
            </span>
         </div>
         <ul role="list" className="my-0 list-none divide-y divide-gray-200 pl-0">
            {data.items.map(page => (
               <FlaggedRow key={page.url} page={page} activeFlag={activeFlag} />
            ))}
         </ul>
      </div>
   );
}

function FlaggedRow({ page, activeFlag }: { page: HelpOutFlaggedPage; activeFlag: string }) {
   return (
      <li
         className={cn(
            'relative flex flex-col items-start gap-2 px-4 py-3 transition-colors hover:bg-gray-50 md:gap-y-1',
            QUEUE_GRID_CLASS
         )}
      >
         {/* Fixed to the Type column's width so Guide and Wiki cut the same
             silhouette down the list, the way /Translate's do. */}
         <Badge className="badge-md badge-gray w-[3.75rem] shrink-0 justify-center">
            <FaIcon name={page.type === 'guide' ? 'screwdriver-wrench' : 'file-lines'} />
            <span className="truncate">{page.type === 'guide' ? _js('Guide') : _js('Wiki')}</span>
         </Badge>
         <div className="min-w-0">
            <a
               href={page.url}
               className="font-medium break-words text-gray-900 no-underline after:absolute after:inset-0 hover:text-brand-500 hover:no-underline"
            >
               {page.name}
            </a>
            {page.device && <div className="text-xs text-gray-500">{page.device}</div>}
         </div>
         {/* The flag you filtered by is picked out, so what stands out is
             the work this page needs on top of the pile you are in. */}
         <div className="flex flex-wrap gap-1">
            {page.flags.map(flag => (
               <Badge
                  key={flag.id}
                  className={cn('badge-md', flag.id === activeFlag ? 'badge-brand' : 'badge-gray')}
               >
                  {flag.icon && <FaIcon name={flag.icon} />}
                  {flag.name}
               </Badge>
            ))}
         </div>
         <span className="text-sm text-gray-600 tabular-nums md:text-right">
            {/* The column header that names this number is hidden below md. */}
            <span className="md:hidden">
               {___p(page.views, '%1 view', '%1 views', localizeAndFormat(page.views))}
            </span>
            <span className="max-md:hidden">{localizeAndFormat(page.views)}</span>
         </span>
      </li>
   );
}

function FlaggedPager({ data, onPage }: { data: FlaggedPageData; onPage: (page: number) => void }) {
   if (data.pageCount <= 1) {
      return null;
   }
   const range = calculatePageRange(data.page, data.pageCount, 5);
   const pages = [];
   for (let i = range.start; i <= range.end; i++) {
      pages.push(i);
   }
   const goTo = (event: ReactMouseEvent, target: number) => {
      event.preventDefault();
      if (target >= 1 && target <= data.pageCount && target !== data.page) {
         onPage(target);
      }
   };
   const href = (target: number) => {
      const url = new URL(window.location.href);
      url.searchParams.set('page', String(target));
      return url.pathname + url.search + url.hash;
   };

   return (
      <div className="mt-4">
         <Pagination>
            <PaginationContent className="list-none pl-0">
               <PaginationItem>
                  <PaginationPrevious
                     href={href(data.page - 1)}
                     aria-disabled={data.page === 1}
                     className={cn(
                        'no-underline',
                        data.page === 1 && 'pointer-events-none opacity-50'
                     )}
                     onClick={event => goTo(event, data.page - 1)}
                  />
               </PaginationItem>
               {pages.map(target => (
                  <PaginationItem key={target}>
                     <PaginationLink
                        href={href(target)}
                        isActive={target === data.page}
                        className="no-underline"
                        onClick={event => goTo(event, target)}
                     >
                        {target}
                     </PaginationLink>
                  </PaginationItem>
               ))}
               <PaginationItem>
                  <PaginationNext
                     href={href(data.page + 1)}
                     aria-disabled={data.page === data.pageCount}
                     className={cn(
                        'no-underline',
                        data.page === data.pageCount && 'pointer-events-none opacity-50'
                     )}
                     onClick={event => goTo(event, data.page + 1)}
                  />
               </PaginationItem>
            </PaginationContent>
         </Pagination>
      </div>
   );
}
